import { get, writable } from "svelte/store";

import { loadModules } from "esri-loader";

import CountryPolygons from "../Map/CountryPolygons";

import LoadState from "../Enums/LoadState";
import GeoHelper from "../Helpers/GeoHelper";
import MapDataSource from "../Map/Enums/MapDataSource";
import MapLayer from "../Map/Enums/MapLayer";
import MapLayerGroup from "../Map/Enums/MapLayerGroup";
import MapLayerManager from "../Map/MapLayerManager";
import { Paint } from "../Map/MapLayerStyles";
import Sanction from "../Models/Sanction";
import SanctionAuthority from "./SanctionAuthority";

const SanctionStore = {
	config: {
		token: "",
		server: "https://services1.arcgis.com/ZURaVO35aTK1Ugmq/ArcGIS/rest/services"
	},
	meta: {
		loadState: writable(LoadState.INIT),
		mapInitted: writable(false)
	},
	results: {
		current: writable([]),
		raw: writable([]),
		byLatLng: writable([]),
		byCC: writable([])
	},
	actions: {
		initMap: (map) => {
			if (get(SanctionStore.meta.mapInitted)) {
				return;
			}
			map.addSource(MapDataSource.SANCTIONS_POLYGONS, {
				type: "geojson",
				data: GeoHelper.emptyGeoJson()
			});
			map.addLayer(
				{
					id: MapLayer.SANCTIONS_POLYGONS,
					source: MapDataSource.SANCTIONS_POLYGONS,
					type: "fill",
					layout: {
						visibility: "none"
					},
					paint: Paint.sanctions
				}
			);
			MapLayerManager.addLayerGroup(MapLayerGroup.SANCTIONS, [MapLayer.SANCTIONS_POLYGONS], [MapDataSource.SANCTIONS_POLYGONS]);

			SanctionStore.meta.mapInitted.set(true);
		},
		deinitMap: () => {
			if (!get(SanctionStore.meta.mapInitted)) {
				return;
			}
			MapLayerManager.removeLayerGroup(MapLayerGroup.SANCTIONS);
			SanctionStore.meta.mapInitted.set(false);
		},
		init: async () => {
			if (get(SanctionStore.meta.loadState) !== LoadState.INIT) {
				// Already initting
				return;
			}
			SanctionStore.meta.loadState.set(LoadState.LOADING);

			const token = SanctionStore.config.token;
			const server = SanctionStore.config.server;

			const countryPolygons = await CountryPolygons.get();
			console.log("countryPolygons", countryPolygons);

			loadModules(["esri/Map", "esri/layers/FeatureLayer", "esri/identity/IdentityManager", "esri/tasks/support/Query"])
				.then((classes) => {
					const FeatureLayer = classes[1];
					const IdentityManager = classes[2];
					const Query = classes[3];

					if (token.length > 0) {
						IdentityManager.registerToken({
							token: token,
							server: server
						});
					}

					const ofacUrl = server + "/OFAC_SanctionUpdates_Table/FeatureServer";
					const ofacSanctionsLayer = new FeatureLayer({ url: ofacUrl });

					const euunUrl = server + "/Sanctions_Table/FeatureServer";
					const euunSanctionsLayer = new FeatureLayer({ url: euunUrl });

					const usUrl = server + "/US_Sanctions_Table/FeatureServer";
					const usSanctionsLayer = new FeatureLayer({ url: usUrl });

					Promise.all([
						ofacSanctionsLayer.queryFeatures(),
						euunSanctionsLayer.queryFeatures(),
						usSanctionsLayer.queryFeatures()
					]).then((responses) => {
						SanctionStore.actions._parseAGOLResults(responses[0], SanctionAuthority.OFAC);
						SanctionStore.actions._parseAGOLResultsUnknownAuthority(responses[1], "EUUN_Adopted_By");
						SanctionStore.actions._parseAGOLResults(responses[2], SanctionAuthority.US);

						// Sort the new geojsons into features. A list of sanctions exists in properties of each feature.
						const allSanctions = get(SanctionStore.results.raw);
						// const featureByLngLat = SanctionStore.helpers.groupByLngLatAndType(allSanctions);
						// SanctionStore.results.byLatLng.set(featureByLngLat);
						const featureByCC = SanctionStore.helpers.groupByCountry(allSanctions);
						SanctionStore.results.byCC.set(featureByCC);

						SanctionStore.meta.loadState.set(LoadState.LOADED);
					});
				});
		},
		_parseAGOLResults: (results, sanctionAuthority) => {
			// Convert arcgis data to geojson.
			const allSanctions = get(SanctionStore.results.raw);
			for (let i = 0; i < results.features.length; i++) {
				const sanction = new Sanction(results.features[i], sanctionAuthority);
				allSanctions.push(sanction);
			}
			SanctionStore.results.raw.set(allSanctions);
		},
		_parseAGOLResultsUnknownAuthority: (results, field = "Adopted_By") => {
			// Convert arcgis data to geojson.
			const allSanctions = get(SanctionStore.results.raw);
			for (let i = 0; i < results.features.length; i++) {
				const feature = results.features[i];
				if (feature.attributes[field].indexOf("EU") >= 0) {
					const sanction = new Sanction(results.features[i], SanctionAuthority.EU);
					allSanctions.push(sanction);
				}
				if (feature.attributes[field].indexOf("US") >= 0) {
					const sanction = new Sanction(results.features[i], SanctionAuthority.US);
					allSanctions.push(sanction);
				}
				if (feature.attributes[field].indexOf("UN") >= 0) {
					const sanction = new Sanction(results.features[i], SanctionAuthority.UN);
					allSanctions.push(sanction);
				}
			}
			SanctionStore.results.raw.set(allSanctions);
		}
	},
	helpers: {
		findByLngLat: (lng, lat) => {
			const index = get(SanctionStore.results.byLatLng).findIndex((item) => {
				return item.properties.lng === lng && item.properties.lat === lat;
			});
			if (index !== -1) {
				return get(SanctionStore.results.byLatLng)[index];
			}
			return null;
		},
		findByCC: (cc) => {
			const index = get(SanctionStore.results.byCC).findIndex((item) => {
				return item.properties.cc === cc;
			});
			if (index !== -1) {
				return get(SanctionStore.results.byCC)[index];
			}
			return null;
		},
		groupByCountry: (sanctions = []) => {
			const reducedIntoCC = [];
			const foundCCs = [];
			for (let i = 0; i < sanctions.length; i++) {
				if (foundCCs.indexOf(sanctions[i].countryCode) === -1) {
					const containerFeature = SanctionStore.helpers.generateCountryPolygonFeature(sanctions[i].countryCode);
					if (containerFeature !== null) {
						reducedIntoCC.push(containerFeature);
						foundCCs.push(sanctions[i].countryCode);

						containerFeature.properties.sanctions = [sanctions[i]];
						// containerFeature.properties.lng = lng;
						// containerFeature.properties.lat = lat;
					}
				}
				else {
					const index = foundCCs.indexOf(sanctions[i].countryCode);
					reducedIntoCC[index].properties.sanctions.push(sanctions[i]);
				}
			}
			return reducedIntoCC;
		},
		groupByLngLatAndType: (sanctions = []) => {
			const reducedIntoLngLatAndTypes = [];
			const foundLngLats = [];
			for (let i = 0; i < sanctions.length; i++) {
				const lng = sanctions[i].lng;
				const lat = sanctions[i].lat;
				const type = sanctions[i].data.geometry.type;
				const lngLatAndType = lng + "," + lat + "," + type;

				if (foundLngLats.indexOf(lngLatAndType) === -1) {
					const containerFeature = SanctionStore.helpers.generateGeometry(sanctions[i].data, sanctions[i].lng, sanctions[i].lat);
					reducedIntoLngLatAndTypes.push(containerFeature);
					foundLngLats.push(lngLatAndType);

					containerFeature.properties.sanctions = [sanctions[i]];
					containerFeature.properties.lng = lng;
					containerFeature.properties.lat = lat;
				}
				else {
					const index = foundLngLats.indexOf(lngLatAndType);
					reducedIntoLngLatAndTypes[index].properties.sanctions.push(sanctions[i]);
				}
			}
			return reducedIntoLngLatAndTypes;
		},
		generateCountryPolygonFeature: (cc) => {
			const source = CountryPolygons.getCountryFeature(cc);
			const feature = JSON.parse(JSON.stringify(source));
			return feature;
		},
		generateGeometry: (feature, lng, lat) => {
			if (feature.geometry.type === "Polygon" || feature.geometry.type === "MultiPolygon") {
				const obj = {
					type: "Feature",
					properties: {},
					geometry: {
						type: feature.geometry.type,
						coordinates: feature.geometry.coordinates
					}
				};
				// Data from AGOL is in Mercator WGS 3857 but needs to be in WGS 4326 (lng,lat)
				GeoHelper.forEachFeatureCoordinateRef(obj, (coords) => {
					coords = GeoHelper.metersToDegrees(coords[0], coords[1]);
					return coords;
				});
				return obj;
			}

			return {
				type: "Feature",
				properties: {},
				geometry: {
					type: "Point",
					coordinates: [lng, lat]
				}
			};

			// console.log("sanctions polygons?", responses[1]);
			// console.log("prev", responses[1].features[0].geometry.rings[0][0][0], responses[1].features[0].geometry.rings[0][0][1]);
			// const newlnglat = GeoHelper.metersToDegrees(responses[1].features[0].geometry.rings[0][0][0], responses[1].features[0].geometry.rings[0][0][1]);
			// console.log("new", newlnglat);
		}
	}
};
export default SanctionStore;
